import { Phone } from '@livecontrol/locale-us';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { Tag } from '../../components';
import { MasqueradeToken } from '../../model';
import { Store, store } from '../../store';
import type { ScheduledEvent } from '../event-details';
import { useContacts } from '../event-details';

interface Props {
  scheduledEvent: ScheduledEvent;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  margin-bottom: 13px;

  .title {
    font-weight: 700;
    font-size: 15px;
    color: rgb(240, 242, 245);
  }
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(176, 186, 209, 0.7);
  text-decoration: none;
  flex-shrink: 0;
  font-size: 13px;
  transition: all var(--transition);

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }
`;

// Same scroll treatment as the Notes tab (scrollbar outside the cards).
const Scroll = styled.div`
  max-height: 350px;
  overflow-y: auto;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContactCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(17, 27, 51, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 13px;

  .priority {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-grid;
    place-items: center;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  .priority.red {
    background: rgba(255, 80, 95, 0.16);
    color: rgb(255, 138, 147);
    border: 1px solid rgba(255, 80, 95, 0.3);
  }

  .priority.amber {
    background: rgba(242, 179, 61, 0.16);
    color: rgb(255, 210, 122);
    border: 1px solid rgba(242, 179, 61, 0.3);
  }

  .priority.green {
    background: rgba(53, 211, 153, 0.16);
    color: rgb(127, 230, 184);
    border: 1px solid rgba(53, 211, 153, 0.3);
  }

  .content {
    flex: 1 1 0%;
    min-width: 0;
  }

  .line1 {
    display: flex;
    align-items: center;
    gap: 9px;
    flex-wrap: wrap;
  }

  .name {
    font-weight: 700;
    font-size: 13px;
    color: rgb(240, 242, 245);
  }

  .line2 {
    margin-top: 6px;
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
  }

  .email {
    font-size: 12px;
    font-weight: 500;
    color: rgb(159, 176, 255);
    text-decoration: underline;
    overflow-wrap: anywhere;
    min-width: 0;
  }

  .phone {
    font-size: 12px;
    font-weight: 500;
    color: rgba(205, 214, 234, 0.78);
  }

  .additional {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 400;
    color: rgba(205, 214, 234, 0.72);
    overflow-wrap: anywhere;
  }
`;

const Empty = styled.div`
  font-size: 13px;
  font-style: italic;
  color: rgba(176, 186, 209, 0.4);
`;

// Highest priority (sorted first) is red, then amber, then green.
const priorityTone = (index: number): string =>
  index === 0 ? 'red' : index === 1 ? 'amber' : 'green';

// preferred_contact_method is free text — map by keyword to a tag color.
const methodClass = (method: string): string => {
  const value = method.toLowerCase();

  if (value.includes('email')) {
    return 'cc-blue';
  }

  if (value.includes('call') || value.includes('phone')) {
    return 'cc-cyan';
  }

  if (value.includes('text') || value.includes('sms')) {
    return 'cc-purple';
  }

  return 'cc-neutral';
};

export const ContactsTab = ({ scheduledEvent }: Props): ReactElement => {
  const { token } = Store.User.useIdentity();
  const { environment } = store.getState();
  const { contacts } = useContacts(scheduledEvent.location?.id, scheduledEvent.location?.user?.id);

  // Same masquerade link the legacy contacts view builds to edit contacts in the dashboard.
  const editUrl = useMemo((): string | undefined => {
    const userId = scheduledEvent.location?.user?.id;

    if (token && userId !== undefined) {
      const decoded = MasqueradeToken.decode(token.jwt);

      return decoded
        ? `${environment.DASHBOARD_URI}/masquerade#token=${decoded.jwt}&xref=${userId}:${userId}&redirect=%2Faccount%2Fusers`
        : undefined;
    }

    return undefined;
  }, [environment, token, scheduledEvent]);

  const list = (contacts ?? []).filter(Boolean);

  return (
    <div>
      <Header>
        <span className='title'>Troubleshooting Contacts</span>

        {editUrl !== undefined && (
          <IconLink href={editUrl} target='_blank' rel='noreferrer' title='Edit contacts in Dashboard'>
            <i className='far fa-external-link' />
          </IconLink>
        )}
      </Header>

      {list.length > 0 ? (
        <Scroll className='cc-scroll'>
          <List>
            {list.map((contact, index) => {
              const {user} = contact;

              const phone =
                Phone.normalize(user?.phone_number)?.format({ parenthetical: true }) ??
                user?.phone_number ??
                '';

              const method = user?.preferred_contact_method ?? '';
              const additional = user?.additional_information ?? '';

              return (
                <ContactCard key={user?.id ?? index}>
                  <span className={`priority ${priorityTone(index)}`}>
                    {contact.onsite_technical_priority ?? index + 1}
                  </span>

                  <div className='content'>
                    <div className='line1'>
                      <span className='name'>{user?.first_name ?? '—'}</span>
                      {method && (
                        <Tag size='small' className={methodClass(method)}>
                          {method}
                        </Tag>
                      )}
                    </div>

                    <div className='line2'>
                      {user?.email && (
                        <a className='email' href={`mailto:${user.email}`}>
                          {user.email}
                        </a>
                      )}
                      {phone && <span className='phone'>{phone}</span>}
                    </div>

                    {additional && <div className='additional'>{additional}</div>}
                  </div>
                </ContactCard>
              );
            })}
          </List>
        </Scroll>
      ) : (
        <Empty>No troubleshooting contacts.</Empty>
      )}
    </div>
  );
};
